import { Link } from 'react-router-dom';
import { MessageCircle, Share2, Mail, Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback, useMemo } from 'react';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [showComingSoon, setShowComingSoon] = useState(null);
    
  // 使用 useMemo 缓存 socialLinks
  const socialLinks = useMemo(() => ({
    wechat: process.env.REACT_APP_WECHAT_URL,
    xiaohongshu: "https://www.xiaohongshu.com/user/profile/68138b7a000000000e01e186" // Your direct link
  }), []); // 空数组 - 永远不会重新创建

  // 生成结构化数据 - 现在可以安全添加 socialLinks 依赖
  useEffect(() => {
    const schemaScript = document.querySelector('#organization-schema');
    if (schemaScript) {
      const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": t('footer.brand_name'),
        "url": window.location.origin,
        "logo": `${window.location.origin}/logo.png`,
        "sameAs": Object.values(socialLinks).filter(Boolean),
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "email": "eurowinetrip2026@foxmail.com",
          "availableLanguage": ["Chinese", "English"]
        }
      };
      schemaScript.textContent = JSON.stringify(organizationSchema);
    }
  }, [t, currentLang, socialLinks]); // 添加 socialLinks 依赖

  // 处理社交媒体点击 - 现在可以安全添加 socialLinks 依赖
  const handleSocialClick = useCallback((platform) => {
    const url = socialLinks[platform];
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setShowComingSoon(platform);
      setTimeout(() => setShowComingSoon(null), 3000);
    }
  }, [socialLinks]); // 添加 socialLinks 依赖

  // 其余代码保持不变...
  const getComingSoonMessage = (platform) => {
    const messages = {
      wechat: currentLang === 'zh' 
        ? '🔔 微信公众号即将开通，敬请期待！' 
        : '🔔 WeChat Official Account coming soon!',
      xiaohongshu: currentLang === 'zh'
        ? '🔔 小红书账号即将开通，敬请期待！'
        : '🔔 Xiaohongshu account coming soon!'
    };
    return messages[platform] || '';
  };

  const socialButtons = [
    {
      key: 'wechat',
      icon: MessageCircle,
      label: t('footer.wechat'),
      title: currentLang === 'zh' ? '微信公众号' : 'WeChat Official Account'
    },
    {
      key: 'xiaohongshu',
      icon: Share2,
      label: t('footer.xhs'),
      title: currentLang === 'zh' ? '小红书' : 'Xiaohongshu (RED)'
    }
  ];

  return (
    <footer 
      key={currentLang} 
      className="bg-[#656D4A] text-white w-full font-serif border-t border-white/10 shrink-0"
      role="contentinfo"
    >
      {/* Schema.org JSON-LD */}
      <script id="organization-schema" type="application/ld+json" />
      
      {/* 临时提示浮层 */}
      {showComingSoon && (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 bg-[#800020] text-white px-6 py-3 rounded-full shadow-lg z-50 animate-fade-in-up">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            <span className="text-sm">{getComingSoonMessage(showComingSoon)}</span>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-center min-h-[8rem] py-10 md:py-0 gap-8 md:gap-0">
          
          {/* LEFT: Branding */}
          <div 
            className="flex flex-col gap-1 text-center md:text-left order-2 md:order-1"
            itemScope 
            itemType="https://schema.org/Organization"
          >
            <p className="text-xl md:text-2xl font-medium tracking-tight">
              © {new Date().getFullYear()} <span itemProp="name">{t('footer.brand_name')}</span>
            </p>
            <p className="opacity-80 text-sm italic tracking-wide" itemProp="slogan">
              {t('footer.tagline')}
            </p>
          </div>

          {/* CENTER: Contact */}
          <div className="flex items-center order-1 md:order-2 w-full md:w-auto justify-center">
            <Link 
              to="/contact" 
              className="flex items-center gap-3 text-white/90 transition-all group px-8 py-3 md:px-6 md:py-2 border border-white/30 rounded-full md:border-white/10 hover:border-[#800020] hover:bg-[#800020] transition-colors duration-300"
              aria-label={t('footer.contact_aria')}
            >
              <Mail 
                strokeWidth={1.5} 
                className="w-6 h-6 md:w-5 md:h-5 group-hover:scale-110 transition-transform duration-300" 
                aria-hidden="true"
              />
              <span className="text-sm md:text-xs uppercase tracking-[0.2em] font-bold">
                {t('footer.contact')}
              </span>
            </Link>
          </div>

          {/* RIGHT: Socials */}
          <nav className="flex items-center gap-8 md:gap-10 order-3" aria-label={t('footer.social_nav')}>
            {socialButtons.map(({ key, icon: Icon, label, title }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleSocialClick(key)}
                className="group flex flex-col items-center gap-1 transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg p-1"
                title={title}
                aria-label={title}
              >
                <Icon 
                  className="w-7 h-7 md:w-6 md:h-6 text-white/70 group-hover:text-[#800020] transition-colors duration-300" 
                  aria-hidden="true" 
                />
                <span className="text-[10px] md:text-[9px] uppercase font-bold tracking-widest opacity-80 group-hover:opacity-100">
                  {label}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate(-50%, 1rem);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.3s ease-out;
        }
      `}</style>
    </footer>
  );
}